import { datadogLogger } from '@fanduel/casino-datadog-logger';
import { useStore } from '@/store/useStore';
import {
  DdAction,
  DdError,
  DdEventAttributes,
  DdEventCategory,
} from '../types';
import { buildTelemetryContext } from '../telemetry-context';

export class Logger {
  private static getSanitizedSessionContext() {
    const sessionContext = useStore.getState().userSessionContext;
    const safeSessionContext = { ...(sessionContext ?? {}) };

    delete safeSessionContext.authToken;
    delete safeSessionContext.loginToken;

    return safeSessionContext;
  }

  private static getCoreContext() {
    const {
      runtime,
      userContext,
      userContextStatus,
      userContextError,
      gameWindowContext,
      customerData,
      customerDataError,
      geolocationContext,
      geolocationStatus,
    } = useStore.getState();
    const telemetry = buildTelemetryContext();

    return {
      userId: telemetry.userId,
      runtime: {
        region: telemetry.region,
        product: telemetry.product,
        platform: telemetry.platform,
        brand: telemetry.brand,
        wrapperType: runtime?.wrapperType,
      },
      userContext: {
        loading: userContextStatus?.loading ?? false,
        errorType: userContextError?.type ?? null,
        hasData: Boolean(userContext?.data),
        accountSettings: {
          countryCode: userContext?.data?.accountSettings?.countryCode,
          lastLoginDate: userContext?.data?.accountSettings?.lastLoginDate,
        },
      },
      gameWindow: {
        gameWindowOpen: gameWindowContext?.gameWindowOpen ?? false,
        audioEnabled: gameWindowContext?.audioEnabled ?? false,
        loadingGetBalance: gameWindowContext?.loadingGetBalance ?? false,
        errorGetBalance: gameWindowContext?.errorGetBalance ?? false,
      },
      customerData: {
        loading: customerData?.loading ?? false,
        packageLoading: customerData?.packageLoading ?? false,
        hasData: Boolean(customerData?.data),
        features: customerData?.features ?? [],
        segment: customerData?.segment ?? '',
        errorType: customerDataError?.type ?? null,
        enabledExperiments: telemetry.enabledExperiments,
      },
      gameContext: {
        gameUid: telemetry.gameUid,
        gameId: telemetry.gameId,
        gameName: telemetry.gameName,
        provider: telemetry.provider,
        rgi: telemetry.rgi,
        gamingSessionTrackingId: telemetry.gamingSessionTrackingId,
      },
      geolocationContext: {
        hasGeolocationToken: Boolean(geolocationContext?.geolocationToken),
      },
      geolocationStatus,
    };
  }

  private static resolveBaseContextByUseCase(useCase: DdEventCategory) {
    const coreContext = this.getCoreContext();
    const sessionContext = this.getSanitizedSessionContext();

    switch (useCase) {
      case DdEventCategory.Geolocation:
      case DdEventCategory.UpdateGeolocation:
        return {
          userId: coreContext.userId,
          runtime: coreContext.runtime,
          gameWindow: coreContext.gameWindow,
          geolocationContext: coreContext.geolocationContext,
          geolocationStatus: coreContext.geolocationStatus,
          userContext: coreContext.userContext,
        };
      case DdEventCategory.UserInteraction:
      case DdEventCategory.Wallet:
        return {
          userId: coreContext.userId,
          runtime: coreContext.runtime,
          gameWindow: coreContext.gameWindow,
          gameContext: coreContext.gameContext,
          userContext: coreContext.userContext,
          customerData: coreContext.customerData,
        };
      case DdEventCategory.Initialization:
      case DdEventCategory.GameContext:
      case DdEventCategory.GameLaunch:
      case DdEventCategory.ProviderIntegration:
      default:
        return {
          ...coreContext,
          sessionContext,
        };
    }
  }

  private static withResolvedContext<T extends DdAction | DdError>(
    event: T,
  ): T {
    return {
      ...event,
      context: {
        ...this.resolveBaseContextByUseCase(event.useCase),
        ...(event.context ?? {}),
      },
    };
  }

  private static scrubSensitiveTokensFromErrorContext(
    context?: Record<string, unknown>,
  ) {
    const errorObj = context?.error as
      | {
          config?: { data?: unknown };
        }
      | undefined;

    if (typeof errorObj?.config?.data !== 'string') return;

    try {
      const dataObj = JSON.parse(errorObj.config.data) as Record<
        string,
        unknown
      >;
      delete dataObj?.loginToken;
      delete dataObj?.token;
      delete dataObj?.authToken;
      delete dataObj?.geolocationToken;
      errorObj.config.data = dataObj;
    } catch {
      errorObj.config.data = '[redacted request body]';
    }
  }

  /** The `@event` attributes: the caller's `event` plus the fields every coded event carries. */
  private static buildEventAttributes(event: DdEventAttributes) {
    const { gameUid, userId, sessionId, region, platform } =
      buildTelemetryContext();
    const asString = (value: unknown) => (value == null ? '' : String(value));

    return {
      ...event,
      game_id: asString(gameUid),
      account_id: asString(userId),
      state: asString(region).toUpperCase(),
      session_id: asString(sessionId),
      platform: asString(platform),
    };
  }

  private static getExperiments() {
    const { enabledExperiments } = buildTelemetryContext();
    return {
      enabled: Array.isArray(enabledExperiments) ? enabledExperiments : [],
    };
  }

  static rumAction(action: DdAction) {
    const actionWithContext = this.withResolvedContext(action);

    datadogLogger.ddRumAction({
      ...actionWithContext,
      experiments: this.getExperiments(),
      payload: {
        ...actionWithContext.payload,
        version: process.env.BUILD_VERSION,
      },
    });
  }

  static rumError(error: DdError) {
    const errorWithContext = this.withResolvedContext(error);
    this.scrubSensitiveTokensFromErrorContext(errorWithContext.context);

    datadogLogger.ddRumError({
      ...errorWithContext,
      experiments: this.getExperiments(),
      payload: {
        ...errorWithContext.payload,
        version: process.env.BUILD_VERSION,
      },
    });
  }

  static logRumAction(action: DdAction) {
    const { description, name, useCase, eventType, service, event, logOnly } =
      action;

    if (process.env.DISABLE_LOGGING === 'true') {
      console.log('LOG ACTION - ', { action });
    } else {
      const actionWithContext = this.withResolvedContext(action);

      // DD logs for accurate tracing
      datadogLogger.ddRumLog({
        name: `[${useCase}] - ${name}`,
        payload: {
          description,
          context: actionWithContext.context,
          useCase,
          eventType,
          service,
          version: process.env.BUILD_VERSION,
          ...(event && { event: this.buildEventAttributes(event) }),
        },
      });

      // Rum events for accurate debuging
      if (!logOnly) this.rumAction(actionWithContext);
    }
  }

  static logRumError(error: DdError) {
    const {
      description,
      name,
      stack,
      useCase,
      eventType,
      service,
      event,
      logOnly,
    } = error;

    const errorWithContext = this.withResolvedContext(error);
    this.scrubSensitiveTokensFromErrorContext(errorWithContext.context);

    if (process.env.DISABLE_LOGGING === 'true') {
      console.log('LOG ERROR - ', { error });
    } else {
      // DD logs for accurate tracing
      datadogLogger.ddRumErrorLog({
        name: `[${useCase}] - ${name}`,
        payload: {
          ...errorWithContext.context,
          description,
          stack,
          eventType,
          useCase,
          errorCode: `${eventType}_${useCase}`,
          service,
          version: process.env.BUILD_VERSION,
          ...(event && { event: this.buildEventAttributes(event) }),
        },
      });

      // Rum events for accurate debuging
      if (!logOnly) this.rumError(errorWithContext);
    }
  }
}
